import React, { useState, useRef } from 'react';
import { Button, Switch, Tooltip } from 'antd';
import { SendOutlined, AppstoreAddOutlined, HistoryOutlined } from '@ant-design/icons';
import { useLanguage } from '../../../contexts/LanguageContext';

const SUGGESTIONS = [
  { key: 's1', labelKey: 'buildApp.suggestion1', fallback: 'WhatsApp 客服自動回覆流程' },
  { key: 's2', labelKey: 'buildApp.suggestion2', fallback: '訂單多步驟審批工作流' },
  { key: 's3', labelKey: 'buildApp.suggestion3', fallback: '新員工入職資料收集表單' },
  { key: 's4', labelKey: 'buildApp.suggestion4', fallback: '電郵通知 + 任務分派流程' },
  { key: 's5', labelKey: 'buildApp.suggestion5', fallback: 'Meta Flow 引導式問卷' },
  { key: 's6', labelKey: 'buildApp.suggestion6', fallback: 'WhatsApp × 電郵混合通知' },
];

const BuildAppWelcome = ({ onStart, loading, onOpenHistory }) => {
  const { t } = useLanguage();
  const [prompt, setPrompt] = useState('');
  const [planFirst, setPlanFirst] = useState(false);
  const textareaRef = useRef(null);

  const label = (key, fallback) => {
    const v = t(key);
    return (!v || v === key) ? fallback : v;
  };

  const handleSend = () => {
    const trimmed = prompt.trim();
    if (!trimmed || loading) return;
    onStart({ userMessage: trimmed, planFirst });
  };

  const handleChip = (fallback) => {
    setPrompt(fallback);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={styles.outer}>
      {/* Apps 歷史按鈕（右上角） */}
      <div style={styles.topBar}>
        <Button
          icon={<HistoryOutlined />}
          size="small"
          onClick={onOpenHistory}
          style={{ borderRadius: 6 }}
        >
          {label('buildApp.appsHistory', 'Apps 歷史')}
        </Button>
      </div>

      <div style={styles.card}>
        {/* Icon + Title */}
        <div style={styles.iconWrap}>
          <AppstoreAddOutlined style={styles.icon} />
        </div>
        <h1 style={styles.title}>{label('buildApp.welcomeTitle', '你想建立什麼流程？')}</h1>
        <p style={styles.subtitle}>{label('buildApp.welcomeSubtitle', '描述業務場景、渠道需求或目標，AI 將引導你逐步完成建置')}</p>

        {/* Input Area */}
        <div style={styles.inputWrapper}>
          <textarea
            ref={textareaRef}
            style={styles.textarea}
            placeholder={label('buildApp.inputPlaceholder', '例如：建立一個 WhatsApp 客服流程，自動分流詢問，並在需要人手時通知同事審批…')}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={4}
            disabled={loading}
          />
          <div style={styles.inputActions}>
            <div style={styles.planToggle}>
              <Switch
                size="small"
                checked={planFirst}
                onChange={setPlanFirst}
                disabled={loading}
              />
              <span style={styles.planToggleLabel}>
                {label('buildApp.planFirst', 'Plan First')}
              </span>
              <Tooltip title={label('buildApp.planFirstHelp', '開啟後 AI 收集完需求會先展示計劃讓你確認，再開始生成')}>
                <span style={styles.helpHint}>?</span>
              </Tooltip>
            </div>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={loading}
              disabled={!prompt.trim()}
              style={styles.sendBtn}
            >
              {label('buildApp.startBtn', '開始建立')}
            </Button>
          </div>
        </div>

        {/* Suggestion Chips */}
        <div style={styles.chipsSection}>
          <p style={styles.chipsLabel}>{label('buildApp.suggestionLabel', '快速開始：')}</p>
          <div style={styles.chips}>
            {SUGGESTIONS.map((s) => (
              <button
                key={s.key}
                style={styles.chip}
                onClick={() => handleChip(label(s.labelKey, s.fallback))}
                disabled={loading}
              >
                {label(s.labelKey, s.fallback)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  outer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100%',
    padding: '40px 16px',
    background: 'linear-gradient(135deg, #f5f0ff 0%, #ede9fe 60%, #f0f4ff 100%)',
    position: 'relative',
  },
  topBar: {
    position: 'absolute',
    top: 16,
    right: 20,
  },
  card: {
    width: '100%',
    maxWidth: 680,
    textAlign: 'center',
  },
  iconWrap: {
    marginBottom: 16,
  },
  icon: {
    fontSize: 48,
    color: '#7234CF',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1a1a2e',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: 15,
    color: '#6b7280',
    margin: '0 0 28px 0',
  },
  inputWrapper: {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(114,52,207,0.10)',
    padding: '16px 16px 12px',
    marginBottom: 24,
    border: '1.5px solid #e9d5ff',
  },
  textarea: {
    width: '100%',
    border: 'none',
    outline: 'none',
    resize: 'none',
    fontSize: 15,
    lineHeight: 1.7,
    color: '#222',
    fontFamily: 'inherit',
    background: 'transparent',
    boxSizing: 'border-box',
  },
  inputActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
    paddingTop: 10,
    borderTop: '1px solid #f3e8ff',
  },
  planToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  planToggleLabel: {
    fontSize: 13,
    color: '#555',
    fontWeight: 500,
  },
  helpHint: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: '#e9d5ff',
    color: '#7234CF',
    fontSize: 11,
    cursor: 'help',
  },
  sendBtn: {
    borderRadius: 8,
    fontWeight: 600,
  },
  chipsSection: {
    textAlign: 'left',
  },
  chipsLabel: {
    fontSize: 13,
    color: '#9ca3af',
    marginBottom: 10,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: 20,
    border: '1.5px solid #d8b4fe',
    background: '#faf5ff',
    color: '#5b21b6',
    fontSize: 13,
    cursor: 'pointer',
    transition: 'all 0.18s',
    fontFamily: 'inherit',
    lineHeight: 1.5,
  },
};

export default BuildAppWelcome;
